import type {
  AdminCertificationsListParams,
  AdminCertificationsQueryInput,
} from '@/api/(admin)/certifications/types';

/**
 * Columns the endpoint matches with several values at once.
 *
 * Everything else is `LIKE '%value%'`, so joining two values with a comma
 * matches nothing — those filters stay single-valued. The set here is exactly
 * the mappings marked `arrayable` in the claims DB layer
 * (`FILTER_COLUMNS` in `modules/claims/core/db/claims/claims-list-filter.ts`).
 */
const MULTI_VALUE_FILTERS = new Set([
  'standardIds',
  'status',
  'type',
  'facilityProcesses',
]);

/**
 * Turns the table's state into the endpoint's parameters.
 *
 * One conversion point, because the query key is the object it returns: the
 * route loader reads its values off the URL and `useTable` reads them off its
 * own state, and the prefetch only lands if both normalise the same way.
 *
 * `page` is the translation that matters — the table counts from 1, the
 * endpoint from 0.
 */
export function toAdminCertificationsParams(
  input: AdminCertificationsQueryInput,
): AdminCertificationsListParams {
  // The sub-tab, not a filter the operator picks: Active and Archived are two
  // views of the same list, as they are on the client's tables.
  //
  // `isRemoved` is pinned off for the same reason the old dashboard's menu
  // linked to `{"isArchived":"0","isRemoved":"0"}` — a removed certificate is
  // gone from both lists until it is asked for by name. Without it, removing
  // wrote the flag and the row stayed put, which read as the write failing.
  const filter: Record<string, string> = {
    isArchived: input.isArchived ? '1' : '0',
    isRemoved: '0',
  };
  for (const [key, values] of Object.entries(input.filters ?? {})) {
    if (!values || values.length === 0) continue;
    filter[key] = MULTI_VALUE_FILTERS.has(key)
      ? values.join(',')
      : (values[0] ?? '');
  }
  return {
    page: Math.max(0, input.page - 1),
    count: input.limit,
    ...(input.sort ? { sort: input.sort } : {}),
    filter: JSON.stringify(filter),
  };
}

/** Serialises already-converted params. */
export function buildAdminCertificationsQs(
  params: AdminCertificationsListParams,
): string {
  const qs = new URLSearchParams();
  qs.set('page', String(params.page));
  qs.set('count', String(params.count));
  if (params.sort) qs.set('sort', params.sort);
  if (params.filter) qs.set('filter', params.filter);
  return qs.toString();
}
